import { EntityLinks, Goal, ID } from '@griply/types';

import { sortItems } from '../sort-items';
import { getGoalParentIds } from './get-goal-parents';

export type SortGoalsHierarchicallyOptions = {
  links: EntityLinks;
};

export const sortGoalsHierarchically = (
  goals: Goal[],
  customOrder: ID[],
  { links }: SortGoalsHierarchicallyOptions,
) => {
  if (!goals.length) {
    return [];
  }

  // A goal is a "root" in this context if its parent is NOT in the list.
  const goalIds = new Set(goals.map((goal) => goal.id));
  const roots: Goal[] = [];
  const childrenMap = new Map<ID, Goal[]>();

  goals.forEach((goal) => {
    // A goal under several parents sorts under the first one present in this list.
    const parentId = getGoalParentIds(goal, { links }).find((id) =>
      goalIds.has(id),
    );
    const parentExists = !!parentId;

    if (!parentExists) {
      roots.push(goal);
      return;
    }

    const existing = childrenMap.get(parentId!) ?? [];
    existing.push(goal);
    childrenMap.set(parentId!, existing);
  });

  const sortedRoots = sortItems(roots, customOrder, 'id');

  // Flatten recursively, sorting children by customOrder at each level so the
  // order (including direction reversals applied to customOrder) propagates
  // uniformly through the tree.
  // `visited` guards against a corrupt parent cycle sending the walk infinite.
  const visited = new Set<ID>();
  const buildList = (parents: Goal[]): Goal[] =>
    parents.flatMap((parent) => {
      if (visited.has(parent.id)) {
        return [];
      }
      visited.add(parent.id);

      const children = childrenMap.get(parent.id);
      if (children?.length) {
        const sortedChildren = sortItems(children, customOrder, 'id');
        return [parent, ...buildList(sortedChildren)];
      }
      return [parent];
    });

  return buildList(sortedRoots);
};
